import { API_TO_APP_DETECTION_MAP } from './ReportConstants';
import { APP_TO_API_DETECTION_MAP } from '../../utils/apiConstants';
import { parseExistingRemarks, extractCommentHistory, extractClosureInfo } from '../../utils/EventService';

// Vehicle alert configuration
const VEHICLE_ALERT_BASE_URL = "http://192.168.29.244:3000";
const VEHICLE_ALERT_ENDPOINTS = {
  OFF_ROUTE: "/api/off-route/off-routes",
  TAMPER: "/api/tamper/tampers"
};

/**
 * Format date to "YYYY-MM-DD HH:mm:ss" format for vehicle alert APIs
 */
const formatDateTime = (date) => {
  const pad = (num) => String(num).padStart(2, '0');
  // DB stores UTC (5:30 behind IST). User picks IST → subtract 5:30 → send UTC.
  // getUTC* on a local-IST Date gives the UTC equivalent automatically.
  const year    = date.getUTCFullYear();
  const month   = pad(date.getUTCMonth() + 1);
  const day     = pad(date.getUTCDate());
  const hours   = pad(date.getUTCHours());
  const minutes = pad(date.getUTCMinutes());
  const seconds = pad(date.getUTCSeconds());
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Fetch off-route alerts for a specific area/groupId
 */
export const fetchOffRouteAlerts = async ({
  groupId,
  startTime,
  endTime
}) => {
  try {
    const formattedStart = formatDateTime(startTime);
    const formattedEnd = formatDateTime(endTime);

    const url = `${VEHICLE_ALERT_BASE_URL}${VEHICLE_ALERT_ENDPOINTS.OFF_ROUTE}?startTime=${encodeURIComponent(formattedStart)}&endTime=${encodeURIComponent(formattedEnd)}&groupId=${groupId}`;

    console.log('🚗 Fetching off-route alerts:', url);

    const response = await fetch(url);
    
    if (!response.ok) {
      console.error(`Off-route API failed for groupId ${groupId}: ${response.status}`);
      return [];
    }

    const responseData = await response.json();
    
    // Handle both direct array and records wrapper
    const data = responseData.records || responseData;
    
    if (!Array.isArray(data)) {
      console.error('Off-route API returned invalid data format:', responseData);
      return [];
    }
    
    console.log(`📍 Processing ${data.length} off-route records for groupId ${groupId}`);
    
    // Transform off-route data to match event structure
    return data.map(alert => ({
      id: `or_${alert.vehicle}_${new Date(alert.time).getTime()}`,
      eventId: `or_${alert.vehicle}_${new Date(alert.time).getTime()}`,
      detectionType: 'OR',
      eventType: 'OR',
      timestamp: new Date(alert.time),
      location: alert.location,
      area: alert.location,
      groupId: alert.groupid,
      
      // Vehicle alert specific fields
      isVehicleAlert: true,
      alertType: 'off-route',
      vehicleNumber: alert.vehicle,
      vehicleTransporter: alert.transporter,
      geofenceName: alert.geofencename,
      allocatedGeofence: alert.geofencename ? [alert.geofencename] : [],
      isLoaded: alert.isLoaded === 'true' || alert.isLoaded === true,
      status: alert.status,
      geofenceId: alert.geofenceid,
      
      // Coordinates for map - store as both object and string
      coordinates: `${alert.latitude}, ${alert.longitude}`,
      currentLocation: {
        lat: parseFloat(alert.latitude),
        lng: parseFloat(alert.longitude)
      },
      
      // Formatted time for display
      readableTime: new Date(alert.time).toLocaleString(),
      
      displayName: `${alert.vehicle} - OFF-ROUTE`,
      severity: 'High',
      remarks: [],
      closeStatus: alert.status === 'resolved' ? 'Closed' : 'Open',
      isClosed: alert.status === 'resolved'
    }));
  } catch (error) {
    console.error('Error fetching off-route alerts:', error);
    return [];
  }
};

/**
 * Fetch tamper alerts for a specific area/groupId
 */
export const fetchTamperAlerts = async ({
  groupId,
  startTime,
  endTime
}) => {
  try {
    const formattedStart = formatDateTime(startTime);
    const formattedEnd = formatDateTime(endTime);

    const url = `${VEHICLE_ALERT_BASE_URL}${VEHICLE_ALERT_ENDPOINTS.TAMPER}?startTime=${encodeURIComponent(formattedStart)}&endTime=${encodeURIComponent(formattedEnd)}&groupId=${groupId}`;

    console.log('🔧 Fetching tamper alerts:', url);

    const response = await fetch(url);
    
    if (!response.ok) {
      console.error(`Tamper API failed for groupId ${groupId}: ${response.status}`);
      return [];
    }

    const data = await response.json();
    
    // Transform tamper data to match event structure
    return data.map(alert => ({
      id: `ta_${alert.devicename}_${new Date(alert.servertime).getTime()}`,
      eventId: `ta_${alert.devicename}_${new Date(alert.servertime).getTime()}`,
      detectionType: 'TA',
      eventType: 'TA',
      timestamp: new Date(alert.servertime),
      location: alert.area,
      area: alert.area,
      groupId: alert.groupid,
      
      // Vehicle alert specific fields
      isVehicleAlert: true,
      alertType: 'tamper',
      vehicleNumber: alert.devicename,
      vehicleTransporter: alert.transporter,
      alertSubType: alert.type,
      geofenceId: alert.geofenceid,
      
      // Coordinates for map - store as both object and string
      coordinates: `${alert.latitude}, ${alert.longitude}`,
      currentLocation: {
        lat: parseFloat(alert.latitude),
        lng: parseFloat(alert.longitude)
      },
      
      // Formatted time for display
      readableTime: new Date(alert.servertime).toLocaleString(),
      
      displayName: `${alert.devicename} - TAMPER`,
      severity: 'High',
      remarks: [],
      closeStatus: 'Open',
      isClosed: false
    }));
  } catch (error) {
    console.error('Error fetching tamper alerts:', error);
    return [];
  }
};

/**
 * Main function to fetch report data from all endpoints
 * Handles both camera events and vehicle alerts
 */
export const fetchReportDataFromEndpoints = async ({
  targetAreas,
  eventTypes,
  currentPage,
  timeFilter,
  loadedEventIds,
  locationTypeValues,
  selectedPriorities,
  selectedCloseStatus
}) => {
  // Only handle camera events - no vehicle alerts in reports
  const cameraEventTypes = eventTypes.length === 0 ? [] : eventTypes.filter(type => !['OR', 'TA'].includes(type));
  
  console.log('📊 Event types breakdown:', {
    camera: cameraEventTypes,
    all: eventTypes
  });

  // Track total events across all sources
  let totalEventsCount = 0;
  
  // Map to track unique events
  const eventsMap = new Map();
  
  // Map to track event fingerprints (for deduplication)
  const eventFingerprints = new Set();

  // Track type-specific counts
  const typeCountsFromFetch = {
    cd: 0,
    pd: 0,
    id: 0,
    vd: 0,
    vc: 0,
    ls: 0,
    us: 0,
    ct: 0, 
    sh: 0,
    ii: 0,
    or: 0,
    ta: 0
  };

  // Enhanced priority filtering function
  const filterEventsByPriority = (events, selectedPriorityValues) => {
    if (!selectedPriorityValues || selectedPriorityValues.includes("All") || selectedPriorityValues.length === 0) {
      return events;
    }
    
    return events.filter(event => {
      let eventPriority = event.severity;
      
      if (!eventPriority) {
        eventPriority = event.priority || event.Priority || "Unassigned";
      }
      
      if (typeof eventPriority === 'string') {
        eventPriority = eventPriority.trim();
      }
      
      const normalizedPriority = normalizePriority(eventPriority);
      return selectedPriorityValues.includes(normalizedPriority);
    });
  };

  // Enhanced close status filtering function
  const filterEventsByCloseStatus = (events, selectedCloseStatusValues) => {
    if (!selectedCloseStatusValues || selectedCloseStatusValues.includes("All") || selectedCloseStatusValues.length === 0) {
      return events;
    }
    
    return events.filter(event => {
      const eventCloseStatus = getEventCloseStatus(event);
      return selectedCloseStatusValues.includes(eventCloseStatus);
    });
  };

  // Helper function to normalize priority values
  const normalizePriority = (priority) => {
    if (!priority || priority === null || priority === undefined) {
      return "Unassigned";
    }
    
    const priorityStr = String(priority).toLowerCase().trim();
    
    switch (priorityStr) {
      case 'high':
      case 'h':
      case '3':
      case 'urgent':
      case 'critical':
        return "High";
      case 'medium':
      case 'moderate':
      case 'm':
      case '2':
      case 'normal':
        return "Moderate";
      case 'low':
      case 'l':
      case '1':
      case 'minor':
        return "Low";
      case 'unassigned':
      case 'none':
      case 'null':
      case '0':
      case '':
        return "Unassigned";
      default:
        if (['High', 'Moderate', 'Low', 'Unassigned'].includes(priority)) {
          return priority;
        }
        return "Unassigned";
    }
  };

  // Function to determine if an event is closed
  const isEventClosed = (event) => {
    if (!event || !event.remarks) return false;
    
    try {
      const parsedComments = parseExistingRemarks(event.remarks);
      
      if (!Array.isArray(parsedComments) || parsedComments.length === 0) return false;

      const closureRemarks = parsedComments.filter(remark =>
        remark.comment && (
          remark.comment.startsWith('EVENT_CLOSED_BY_USER:') ||
          remark.comment.startsWith('EVENT_REOPENED_BY_USER:')
        )
      );

      if (closureRemarks.length === 0) return false;

      const lastAction = closureRemarks[0];
      return lastAction.comment.startsWith('EVENT_CLOSED_BY_USER:');
    } catch (error) {
      console.warn('Error checking event close status:', error);
    }
    
    return false;
  };

  // Helper function to get event close status
  const getEventCloseStatus = (event) => {
    return isEventClosed(event) ? 'Closed' : 'Open';
  };

  // ==================== FETCH CAMERA EVENTS ====================
  if (cameraEventTypes.length > 0 || eventTypes.length === 0) {
    console.log('📹 Fetching camera events...');
    console.log('🎯 Target areas:', targetAreas);
    
    // Create a mapping between endpoints and their target groupIds
    const endpointGroupMap = {};
    targetAreas.forEach(area => {
      if (!endpointGroupMap[area.endpoint]) {
        endpointGroupMap[area.endpoint] = [];
      }
      endpointGroupMap[area.endpoint].push(area.groupId);
    });
    
    console.log('📍 Endpoint group map:', endpointGroupMap);
    
    // Determine perPage based on number of servers
    const endpointCount = Object.keys(endpointGroupMap).length;
    const perPageByServer = endpointCount > 1 ? Math.floor(400 / endpointCount) : 250;
    
    for (const [endpoint, groupIds] of Object.entries(endpointGroupMap)) {
      try {
        const requestBody = {
          deviceId: 0, 
          locationType: locationTypeValues || ["ALL"],
          from: timeFilter.startTime.toISOString(),
          to: timeFilter.endTime.toISOString(),
          eventTypes: cameraEventTypes,
          page: currentPage,
          perPage: perPageByServer,
          sort: "DESC"
        };
        
        const apiUrl = `https://${endpoint}/api/va/events`;
        
        console.log(`🔗 API URL for endpoint "${endpoint}":`, apiUrl);
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });
        
        if (!response.ok) {
          console.error(`API request failed for endpoint ${endpoint} with status ${response.status}`);
          continue;
        }
        
        const responseData = await response.json();
        const events = responseData.data || responseData;
        const totalItems = responseData.total || events.length;

        totalEventsCount += totalItems;
        
        if (responseData.eventTypeCounts && Array.isArray(responseData.eventTypeCounts)) {
          responseData.eventTypeCounts.forEach(typeCount => {
            const appDetectionType = API_TO_APP_DETECTION_MAP[typeCount.eventType]?.toLowerCase();
            if (appDetectionType && typeCountsFromFetch[appDetectionType] !== undefined) {
              typeCountsFromFetch[appDetectionType] += typeCount.count;
            }
          });
        }
        
        // Filter events by groupIds first
        const groupFilteredEvents = events.filter(event => groupIds.includes(event.groupId));
        const priorityFilteredEvents = filterEventsByPriority(groupFilteredEvents, selectedPriorities);
        const finalFilteredEvents = filterEventsByCloseStatus(priorityFilteredEvents, selectedCloseStatus);
        
        console.log(`📊 Camera filtering results for endpoint ${endpoint}:`, {
          original: events.length,
          afterGroupFilter: groupFilteredEvents.length,
          afterPriorityFilter: priorityFilteredEvents.length,
          afterCloseStatusFilter: finalFilteredEvents.length
        });
        
        for (const event of finalFilteredEvents) {
          const eventId = event.eventId;
          
          if (eventsMap.has(eventId) || loadedEventIds.has(eventId)) continue;
          
          let eventArea = "Unknown";
          for (const area of targetAreas) {
            if (area.groupId === event.groupId) {
              eventArea = area.name;
              break;
            }
          }
          
          const parsedComments = parseExistingRemarks(event.remarks);
          const commentHistory = extractCommentHistory(event.remarks);
          const closureInfo = extractClosureInfo(event.remarks);
          
          const eventSeverity = normalizePriority(event.severity || event.priority || event.Priority);
          const closeStatus = getEventCloseStatus(event);
          
          const formattedEvent = {
            id: eventId, 
            eventId: eventId, 
            detectionType: event.eventType,
            timestamp: new Date(event.createdAt),
            location: event.attributes?.location || event.location || "Unknown",
            area: eventArea,
            groupId: event.groupId,
            cameraIp: event.cameraIp,
            imageUrl: `https://${endpoint}/api/va/event?id=${eventId}`,
            httpsUrl: `https://${endpoint}`,
            displayName: `${event.eventType} at ${event.location || "Unknown"} (${eventArea})`,
            severity: eventSeverity, 
            
            remarks: parsedComments, 
            commentHistory: commentHistory,
            closureInfo: closureInfo, 
            totalComments: parsedComments.length,
            userComments: commentHistory.length,
            
            closeStatus: closeStatus,
            isClosed: closeStatus === 'Closed',
            
            // Original event for reference
            originalEvent: event
          };
          
          const fingerprint = JSON.stringify({
            type: event.eventType,
            time: event.createdAt,
            location: event.attributes?.location || event.location || "Unknown",
            area: eventArea,
            groupId: event.groupId,
            cameraIp: event.cameraIp
          });
          
          if (!eventFingerprints.has(fingerprint)) {
            eventsMap.set(eventId, formattedEvent);
            eventFingerprints.add(fingerprint);
          }
        }
        
      } catch (err) {
        console.error(`Error fetching camera events from endpoint ${endpoint}:`, err);
      }
    }
  }
  
  // Convert map to array
  const newEvents = Array.from(eventsMap.values());
  const totalReturnedEvents = newEvents.length;
  
  // Enhanced logging with statistics
  const eventsWithComments = newEvents.filter(e => e.userComments > 0).length;
  const closedEvents = newEvents.filter(e => e.isClosed).length;
  const totalUserComments = newEvents.reduce((sum, e) => sum + (e.userComments || 0), 0);
  const vehicleAlertCount = newEvents.filter(e => e.isVehicleAlert).length;
  
  console.log(`📈 Final fetch results:`, {
    totalEventsCount,
    newEventsReturned: totalReturnedEvents,
    cameraEvents: totalReturnedEvents - vehicleAlertCount,
    vehicleAlerts: vehicleAlertCount,
    offRouteAlerts: typeCountsFromFetch.or,
    tamperAlerts: typeCountsFromFetch.ta,
    closeStatusDistribution: {
      open: newEvents.filter(e => !e.isClosed).length,
      closed: closedEvents
    },
    commentStatistics: {
      eventsWithComments,
      totalUserComments,
      avgCommentsPerEvent: totalReturnedEvents > 0 ? (totalUserComments / totalReturnedEvents).toFixed(2) : 0
    }
  });
  
  return {
    newEvents,
    totalEventsCount,
    totalReturnedEvents,
    typeCountsFromFetch,
    commentStatistics: {
      eventsWithComments,
      totalUserComments,
      closedEvents
    }
  };
};